import React, { useState } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = (() => {
  const url = process.env.REACT_APP_SUPABASE_URL;
  const key = process.env.REACT_APP_SUPABASE_ANON_KEY;
  return url && key ? createClient(url, key) : null;
})();

const ACCENT = '#F59E0B';
const BG = '#0d0d1a';

const DEFAULT_OPTIONS = [
  { label: 'Yes', value: 'yes', description: "They're in the wrong" },
  { label: 'No', value: 'no', description: "They're not in the wrong" },
];

const DEFAULT_WEIGHTS = {
  yes: { empathy: 0.5, accountability: 0.5, autonomy: -0.4, harmony: 0.2, pragmatism: -0.1 },
  no: { empathy: -0.1, accountability: -0.2, autonomy: 0.6, harmony: -0.1, pragmatism: 0.4 },
};

export default function InTheWrongAdd() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!title.trim() || !body.trim() || submitting) return;

    setSubmitting(true);
    setError(null);

    if (!supabase) {
      setError('Database not configured.');
      setSubmitting(false);
      return;
    }

    const { error: dbError } = await supabase.from('vote_questions').insert({
      title: title.trim(),
      body: body.trim(),
      source: 'custom',
      options: DEFAULT_OPTIONS,
      question_type: 'yes_no',
      personality_weights: DEFAULT_WEIGHTS,
      is_active: true,
    });

    if (dbError) {
      setError(dbError.message);
    } else {
      setSubmitted(true);
    }
    setSubmitting(false);
  }

  function handleAnother() {
    setTitle('');
    setBody('');
    setSubmitted(false);
    setError(null);
  }

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: 12,
    border: '1.5px solid rgba(255,255,255,0.15)',
    background: 'rgba(255,255,255,0.04)',
    color: 'white',
    fontFamily: 'Nunito, sans-serif',
    fontSize: 15,
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: BG,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '28px 16px 72px',
      fontFamily: 'Nunito, sans-serif',
    }}>
      <div style={{ width: '100%', maxWidth: 600, marginBottom: 24 }}>
        <a href="/in-the-wrong" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Nunito, sans-serif', fontSize: 13, textDecoration: 'none', fontWeight: 700 }}>
          ← In The Wrong?
        </a>
      </div>

      <div style={{ width: '100%', maxWidth: 600, background: 'rgba(255,255,255,0.04)', borderRadius: 24, border: '1px solid rgba(255,255,255,0.08)', padding: '32px 28px' }}>
        <h1 style={{ fontFamily: "'Fredoka One', cursive", fontSize: 26, color: ACCENT, margin: '0 0 6px' }}>
          Add a Question
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, margin: '0 0 28px' }}>
          Describe the situation. Keep it concise — a few sentences is enough.
        </p>

        {submitted ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 44, marginBottom: 12 }}>✅</div>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 16, marginBottom: 24, fontWeight: 700 }}>
              Question added!
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={handleAnother}
                style={{ padding: '12px 24px', borderRadius: 50, border: `1.5px solid ${ACCENT}`, background: 'transparent', color: ACCENT, cursor: 'pointer', fontFamily: "'Fredoka One', cursive", fontSize: 16 }}
              >
                Add another
              </button>
              <a
                href="/in-the-wrong"
                style={{ padding: '12px 24px', borderRadius: 50, border: 'none', background: ACCENT, color: BG, cursor: 'pointer', fontFamily: "'Fredoka One', cursive", fontSize: 16, textDecoration: 'none', display: 'inline-block' }}
              >
                Go vote →
              </a>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>
                Title
              </label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g. I refused to pay for my sister's wedding cake"
                maxLength={200}
                required
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = ACCENT}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.15)'}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>
                The situation
              </label>
              <textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                placeholder="Describe what happened..."
                rows={6}
                required
                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }}
                onFocus={e => e.target.style.borderColor = ACCENT}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.15)'}
              />
            </div>

            {error && (
              <p style={{ color: '#F87171', fontSize: 13, marginBottom: 16 }}>{error}</p>
            )}

            <button
              type="submit"
              disabled={submitting || !title.trim() || !body.trim()}
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: 50,
                border: 'none',
                background: submitting || !title.trim() || !body.trim() ? 'rgba(245,158,11,0.3)' : ACCENT,
                color: BG,
                fontFamily: "'Fredoka One', cursive",
                fontSize: 17,
                cursor: submitting || !title.trim() || !body.trim() ? 'default' : 'pointer',
                letterSpacing: 0.5,
                transition: 'background 0.15s',
              }}
            >
              {submitting ? 'Adding...' : 'Add Question'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
